/**
 * useSprayPickerSources — the numbers on the Spraywall wall picker's cards,
 * kept at app level so a tab switch loses nothing.
 * docs/spec/01f-tab-loading.md, "useSprayPickerSources";
 * docs/spec/21j-spraywall-tab-navigation.md, "The wall card — SprayWallCard", "Data".
 *
 * What it holds, per wall: the active-route count, the "new this week" count
 * and the attention counts (open reports and routes to review — the whole wall
 * for a moderator, the viewer's own routes for any other signed-in viewer),
 * plus the viewer's own `sprayAscents` summary (`mine`). The reads are the
 * exported fetch functions of `useSprayWallCardStats.js`, unchanged: a count
 * is `undefined` while counting, `null` on an error (active count only), 0 on
 * an error for the others.
 *
 * ## Rules (the same ones `useRangeCache` states for the leaderboard)
 *
 * - **Freshness.** The counts live 5 minutes, `mine` 60 seconds. `ensure()`
 *   serves a younger value as it is; an older or missing one is read while the
 *   kept one stays on the card (a card never goes back to counting).
 * - **Per-wall re-read.** A wall that is new, or whose `currentVersionId`
 *   changed, is read again at once whatever its age.
 * - **Invalidation.** `invalidate()` forgets the counts' age (the tab calls it
 *   when a wall page opens — the viewer may add or moderate routes there); a
 *   change of today's own spray entries forgets the age of `mine`. A read that
 *   was already in flight when `invalidate()` ran does not stamp its result as
 *   fresh: its answer may predate the change.
 * - **Scope switch.** The values belong to one (gym, user, moderator) scope. A
 *   render for another scope derives empty values from the mismatch, and a
 *   read still in flight for the previous scope is discarded when it resolves
 *   (the generation counter) — gym A's counts never reach gym B.
 *
 * ## Why plain state + refs, not `useRangeCache`
 *
 * `useRangeCache` keeps ONE value per instance, keyed by a range. The picker
 * needs one count group per wall with its own age and its own `currentVersionId`
 * — a version change must re-read that wall, not the gym. Per-wall keys inside
 * a single range key would re-read every wall on every change, so the store is
 * a slot of per-wall maps with per-wall age records in refs. The rules above
 * are the ones `useRangeCache` states (status, scope switch, freshness,
 * invalidate), written for per-wall keys.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { fetchActiveRouteCount, fetchNewThisWeekCount } from "./useSprayActiveRouteCount";
import { fetchOpenReportCount, fetchNeedsReviewCount, fetchOwnAttention } from "./useSprayWallCardStats";
import { summarizeAscentsByWall } from "../utils/sprayWallCardModel";

/** A count changes rarely; the viewer's own changes happen on a wall page (which invalidates). */
export const COUNTS_FRESH_MS = 5 * 60 * 1000;
/** The viewer's own ascent summary — own data, kept short. */
export const MINE_FRESH_MS = 60 * 1000;

const EMPTY_WALLS = Object.freeze([]);

/**
 * The viewer's `sprayAscents` grouped per wall: a Map, or `null` on an error.
 * One query for every wall and version; grouping happens here, so the picker
 * costs one read per ascent instead of a query per card.
 */
function fetchOwnAscentSummary({ gymId, uid }) {
  return Promise.resolve()
    .then(() => getDocs(query(collection(db, "gyms", gymId, "sprayAscents"), where("userId", "==", uid))))
    .then((snap) => summarizeAscentsByWall(snap.docs.map((d) => d.data())), () => null);
}

const wallVersion = (w) => w.currentVersionId || null;

/**
 * A cheap signature of today's own spray entries: each entry's id with the
 * fields that change when the viewer logs a send, a project, a repeat or a
 * flash. Not the whole document — a `serverTimestamp` landing on it must not
 * count as a change.
 */
export function spraySignature(todayClimbLog) {
  const spray = todayClimbLog?.spray;
  if (!spray || typeof spray !== "object") return "";
  return Object.keys(spray).sort().map((id) => {
    const e = spray[id] || {};
    return `${id}:${e.kind || ""}:${e.attempts ?? ""}:${e.flash === true ? 1 : 0}`;
  }).join("|");
}

// Shared empties keep the returned object stable while a scope mismatch lasts.
const EMPTY = Object.freeze({});
const freshSlot = (scope) => ({
  scope,
  counts: EMPTY, newCounts: EMPTY, reportCounts: EMPTY, reviewCounts: EMPTY,
  mine: null,
  pending: 0,          // reads in flight
  delivered: false,    // anything has landed in this scope
  ensured: false,      // `ensure` has run in this scope
});

/**
 * @param {object} opts
 * @param {string|null} opts.gymId
 * @param {string|null} opts.uid              null = signed out (no `mine`, no own attention)
 * @param {boolean}     opts.canModerate      per-wall report / review counts instead of the own attention
 * @param {object|null} opts.todayClimbLog    today's own climb log (the spray half is watched)
 */
export function useSprayPickerSources({ gymId = null, uid = null, canModerate = false, todayClimbLog = null } = {}) {
  const scope = gymId ? `${gymId}|${uid || ""}|${canModerate ? "m" : ""}` : null;
  const [slot, setSlot] = useState(() => freshSlot(scope));

  // The refs follow the scope during render, before any effect of this commit
  // (the picker's first `ensure`) can run for the new scope. Idempotent, so a
  // double render is safe.
  const scopeRef = useRef(scope);
  const genRef = useRef(0);
  const ctxRef = useRef({ gymId, uid, canModerate });
  // Per wall: { versionId, at, token, inFlight } — the age record of its count group.
  const wallsMetaRef = useRef(new Map());
  // The non-moderator's own-attention read (one for all walls).
  const attentionRef = useRef({ key: null, at: 0, token: 0, inFlight: false });
  const mineRef = useRef({ at: 0, token: 0, inFlight: false, epoch: 0 });
  // Bumped by `invalidate()`: a read that started before it is not stamped fresh.
  const countsEpochRef = useRef(0);
  const tokenRef = useRef(0);
  if (scopeRef.current !== scope) {
    scopeRef.current = scope;
    genRef.current += 1;
    wallsMetaRef.current = new Map();
    attentionRef.current = { key: null, at: 0, token: 0, inFlight: false };
    mineRef.current = { at: 0, token: 0, inFlight: false, epoch: 0 };
  }
  ctxRef.current = { gymId, uid, canModerate };

  // Applies `fn` to the slot of the scope it was started for. A slot of another
  // scope is replaced by a fresh one first: the first read of a new scope can
  // land before any reset effect would have run.
  const update = useCallback((gen, forScope, fn) => {
    if (gen !== genRef.current) return; // another scope by now
    setSlot((s) => fn(s.scope === forScope ? s : freshSlot(forScope)));
  }, []);

  const ensure = useCallback((walls) => {
    const { gymId: gid, uid: userId, canModerate: moderate } = ctxRef.current;
    const forScope = scopeRef.current;
    if (!gid || !forScope) return Promise.resolve();
    const gen = genRef.current;
    const list = Array.isArray(walls) ? walls : EMPTY_WALLS;
    const now = Date.now();
    const jobs = [];

    // `pending` brackets every read, so `status` can say refreshing / ready.
    const begin = () => update(gen, forScope, (s) => ({ ...s, ensured: true, pending: s.pending + 1 }));
    const finish = (patch) => update(gen, forScope, (s) => ({
      ...s, ...patch(s), delivered: true, pending: Math.max(0, s.pending - 1),
    }));
    // A first call with nothing to read still marks the scope as ensured (a
    // gym without walls is settled, not idle). Same object back = no render.
    update(gen, forScope, (s) => (s.ensured ? s : { ...s, ensured: true }));

    // No walls, nothing to show: a prefetch that runs before the walls arrive
    // must not spend the own-attention and ascent reads on an empty picker.
    if (list.length === 0) return Promise.resolve();

    for (const w of list) {
      const versionId = wallVersion(w);
      const meta = wallsMetaRef.current.get(w.id);
      const versionChanged = meta && meta.versionId !== versionId;
      const stale = !meta || versionChanged || meta.at === 0 || now - meta.at >= COUNTS_FRESH_MS;
      // A read in flight for this very version is waited for, not duplicated.
      if (!stale || (meta?.inFlight && !versionChanged)) continue;

      const token = ++tokenRef.current;
      const epoch = countsEpochRef.current;
      const rec = { versionId, at: meta?.at || 0, token, inFlight: true };
      wallsMetaRef.current.set(w.id, rec);
      // A result applies only while it is the newest read of this wall — a
      // version change started a newer one, which must not be overwritten.
      const live = () => gen === genRef.current && wallsMetaRef.current.get(w.id)?.token === token;
      const into = (key) => (n) => {
        if (live()) update(gen, forScope, (s) => ({ ...s, [key]: { ...s[key], [w.id]: n }, delivered: true }));
      };
      const reads = [
        fetchActiveRouteCount({ gymId: gid, wallId: w.id, versionId }).then(into("counts")),
        fetchNewThisWeekCount({ gymId: gid, wallId: w.id, versionId }).then(into("newCounts")),
      ];
      if (moderate) {
        reads.push(
          fetchOpenReportCount({ gymId: gid, wallId: w.id }).then(into("reportCounts")),
          fetchNeedsReviewCount({ gymId: gid, wallId: w.id, versionId }).then(into("reviewCounts")),
        );
      }
      begin();
      jobs.push(Promise.all(reads).catch(() => {}).then(() => {
        if (gen !== genRef.current) return;
        const cur = wallsMetaRef.current.get(w.id);
        if (cur?.token === token) {
          cur.inFlight = false;
          cur.at = epoch === countsEpochRef.current ? Date.now() : 0;
        }
        finish(() => ({}));
      }));
    }

    // The author's own flagged routes: one read for every wall, summarised per
    // wall against that wall's live version — so it follows the wall key too.
    if (!moderate && userId) {
      const key = list.map((w) => `${w.id}:${wallVersion(w) || ""}`).join("|");
      const att = attentionRef.current;
      const stale = att.key !== key || att.at === 0 || now - att.at >= COUNTS_FRESH_MS;
      if (stale && !(att.inFlight && att.key === key)) {
        const token = ++tokenRef.current;
        const epoch = countsEpochRef.current;
        attentionRef.current = { key, at: att.at, token, inFlight: true };
        begin();
        jobs.push(fetchOwnAttention({ gymId: gid, uid: userId, walls: list }).catch(() => null).then((own) => {
          if (gen !== genRef.current) return;
          const cur = attentionRef.current;
          const newest = cur.token === token;
          if (newest) {
            cur.inFlight = false;
            if (own) cur.at = epoch === countsEpochRef.current ? Date.now() : 0;
          }
          finish(() => (newest && own
            ? { reportCounts: own.reportCounts, reviewCounts: own.reviewCounts }
            : {}));
        }));
      }
    }

    // The viewer's own ascents: shorter window, own invalidation.
    if (userId) {
      const m = mineRef.current;
      const stale = m.at === 0 || now - m.at >= MINE_FRESH_MS;
      if (stale && !m.inFlight) {
        const token = ++tokenRef.current;
        const epoch = m.epoch;
        mineRef.current = { ...m, token, inFlight: true };
        begin();
        jobs.push(fetchOwnAscentSummary({ gymId: gid, uid: userId }).then((summary) => {
          if (gen !== genRef.current) return;
          const cur = mineRef.current;
          cur.inFlight = false;
          // A failed re-read keeps the summary on screen and leaves the age
          // alone, so the next `ensure` tries again.
          if (summary) cur.at = epoch === cur.epoch ? Date.now() : 0;
          finish((s) => ({ mine: summary || s.mine }));
        }));
      }
    }

    return Promise.all(jobs).then(() => undefined);
  }, [update]);

  /** Forget the counts' age (keep the values): the next `ensure` reads again. */
  const invalidate = useCallback(() => {
    countsEpochRef.current += 1;
    for (const meta of wallsMetaRef.current.values()) meta.at = 0;
    attentionRef.current.at = 0;
  }, []);

  // A change of today's own spray entries makes `mine` stale. The first value
  // seen is the baseline, not a change.
  const signature = spraySignature(todayClimbLog);
  const signatureRef = useRef(signature);
  useEffect(() => {
    if (signatureRef.current === signature) return;
    signatureRef.current = signature;
    mineRef.current.at = 0;
    mineRef.current.epoch += 1;
  }, [signature]);

  // The background prefetch: the walls the picker would show are the caller's
  // to name — this hook never sees the wall list.
  const prefetch = useCallback((walls) => ensure(walls), [ensure]);

  const current = slot.scope === scope;
  const view = current ? slot : freshSlot(scope);
  let status = "idle";
  if (view.ensured) {
    if (view.pending > 0) status = view.delivered ? "refreshing" : "loading";
    else status = "ready";
  }

  return useMemo(() => ({
    counts: view.counts,
    newCounts: view.newCounts,
    reportCounts: view.reportCounts,
    reviewCounts: view.reviewCounts,
    mine: view.mine,
    status,
    ensure,
    invalidate,
    prefetch,
    // `view` is a new object only for a scope mismatch; its fields are the
    // stable ones to depend on.
  }), [view.counts, view.newCounts, view.reportCounts, view.reviewCounts, view.mine, status, ensure, invalidate, prefetch]);
}
